// @owners { team: patients }
import { ActionSheetContext, Description, InlineAlert, LgPadding } from '@alto/design-system';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  type PlaidLinkError,
  type PlaidLinkOnEventMetadata,
  type PlaidLinkOnExitMetadata,
  type PlaidLinkOnSuccessMetadata,
  type PlaidLinkStableEvent,
  usePlaidLink,
} from 'react-plaid-link';
import { type PaymentMethodType } from '~shared/constants';
import { useAnalytics } from '~shared/hooks';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getPlaidEvent } from '~shared/lib/analytics/src/getPlaidEvent';
import { useCreateBankAccountPaymentMethod } from '~shared/queries/usePaymentMethodMutations';
import { Sentry } from '~shared/sentry';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useCreateLinkToken } from '../../plaid';
import { PlaidBankAccountListItem } from './PlaidBankAccountListItem';

type Props = {
  readonly paymentMethodType: PaymentMethodType;
  readonly setErrorMessage: (errorMessage: string) => void;
};

type PlaidLinkSessionProps = {
  readonly token: string | null;
  readonly onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => Promise<void>;
  readonly onExit: (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => void;
  readonly onEvent: (eventName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => void;
};

const PlaidLinkSession = ({ token, onSuccess, onExit, onEvent }: PlaidLinkSessionProps) => {
  const { ready, open, error } = usePlaidLink({
    onEvent,
    onSuccess: (publicToken, metadata) => {
      void onSuccess(publicToken, metadata);
    },
    onExit,
    token,
  });
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (!ready || error || hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    open();
  }, [error, open, ready]);

  if (error) {
    return (
      <LgPadding>
        <InlineAlert type="error">
          <Description>
            Something went wrong while loading Plaid. Please try again later, or message us if the problem doesn't
            resolve.
          </Description>
        </InlineAlert>
      </LgPadding>
    );
  }

  return (
    <PlaidBankAccountListItem
      loading={!ready}
      onPress={
        ready
          ? () => {
              open();
            }
          : undefined
      }
    />
  );
};

export const PlaidListNavigation = ({ paymentMethodType, setErrorMessage }: Props) => {
  const dispatch = useDispatchShared();
  const { mutateAsync: createBankAccountPaymentMethod } = useCreateBankAccountPaymentMethod();
  const { trackEvent } = useAnalytics();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { createLinkToken } = useCreateLinkToken();
  const { token } = useSelectorShared((state) => state.paymentMethods.plaidLinkToken);
  const [isPlaidRequested, setIsPlaidRequested] = useState(false);

  // get plaid link token if one isn't in redux store
  useEffect(() => {
    if (token) return;

    createLinkToken();
  }, [createLinkToken, token]);

  const sendPlaidAnalytics = useCallback(
    (eventName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
      // @ts-expect-error TS(2322): eventName should be PlaidLinkStableEvent which is included in PlaidEventName (Delete me to see the full error)
      const event = getPlaidEvent(eventName, metadata);

      if (event) {
        dispatch(sendAnalyticsEvent(event));
      }
    },
    [dispatch],
  );

  const onSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        await createBankAccountPaymentMethod({
          publicToken,
          accountId: metadata.accounts[0].id,
          institutionName: metadata.institution?.name || '',
          paymentMethodType,
        });
        closeActionSheet();
        trackEvent({
          event: EVENTS.PAYMENT_METHOD_CREATED,
          params: {
            stripeLinkFormEnabled: false,
            type: 'PlaidData',
          },
        });
        setErrorMessage('');
      } catch {
        setErrorMessage(
          'There was an error creating your payment method. Please try again or message support if the problem persists.',
        );
        trackEvent({
          event: EVENTS.CREATE_PAYMENT_METHOD_FAILED,
          params: {
            stripeLinkFormEnabled: false,
          },
        });
      }
    },
    [closeActionSheet, createBankAccountPaymentMethod, paymentMethodType, setErrorMessage, trackEvent],
  );

  const handlePlaidExit = (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
    // Link tears down its handler on exit. Drop the session so the next tap remounts
    // usePlaidLink and Plaid.create runs again — otherwise open() throws "Create was not called".
    setIsPlaidRequested(false);

    if (!error) return;

    if (error.error_code === 'INVALID_LINK_TOKEN') {
      createLinkToken();
      return;
    }

    Sentry.captureMessage('Error linking account via Plaid', {
      contexts: { 'Plaid Error': { error, metadata } },
      level: 'warning',
    });
    setErrorMessage(`${error.display_message}. Please try again or message support if the problem persists.`);
  };

  const handleItemPress = useCallback(() => {
    trackEvent({ event: EVENTS.ADD_PAYMENT_METHOD_TYPE_SELECTED, params: { type: 'bank_account' } });
    setIsPlaidRequested(true);
  }, [trackEvent]);

  if (!isPlaidRequested) {
    return <PlaidBankAccountListItem onPress={handleItemPress} />;
  }

  return (
    <PlaidLinkSession
      token={token}
      onSuccess={onSuccess}
      onExit={handlePlaidExit}
      onEvent={sendPlaidAnalytics}
    />
  );
};
